import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {useState} from 'react';
import axios from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {LedgerType} from './types';
import {fCurrency} from '../../utils/formatNumber';
import {
  fMonthDayYearSlashNotation,
  fMonthDayYearTimeSlashNotation,
} from '../../utils/formatTime';
import Iconify from '../../../minimals/components/iconify/Iconify';
import {useUserContext} from "../../contexts/UserContext";
import EditPendingTransactionModal from "./EditPendingTransactionModal";
import EditLedgerTransferModal from "./EditLedgerTransferModal";


export const statusColorMap: {
  [key: string]:
      | 'default'
      | 'error'
      | 'primary'
      | 'secondary'
      | 'info'
      | 'success'
      | 'warning'
      | undefined;
} = {
  Pending: 'warning',
  Posted: 'success',
  Voided: 'default',
  Deleted: 'error'
};


function CheckPrintIcon({orderNumber, transactionId, refreshTransactions}: {orderNumber: string, transactionId: string, refreshTransactions: () => void}){
  const [isPrinting, setIsPrinting] = useState(false);
  const user = useUserContext()?.user;

  function downloadBase64Pdf(base64String: string): void {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'check.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    refreshTransactions();
  }
  
  const printCheck = async () => {
    setIsPrinting(true);
    const { data } = await axios.get(`/proxy/api/ledgers/printcheck?ordernumber=${orderNumber}&transactionId=${transactionId}&printedBy=${user?.name}`);
    const { base64Document } = data;
    downloadBase64Pdf(base64Document);
    setIsPrinting(false);
  }
  
  if(!isPrinting) {
    return <IconButton onClick={printCheck}>
      <Iconify icon='eva:printer-outline'/>
    </IconButton>
  }
  return <CircularProgress size={24} />
}

function CancelPendingWireButton({
  orderNumber,
  transaction,
  refreshTransactions,
  refreshBalance
}: {
  orderNumber: string;
  transaction: LedgerType;
  refreshTransactions: () => void;
  refreshBalance: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [isCanceling, setIsCanceling] = useState(false);
  const user = useUserContext()?.user;
  const wireType = transaction.isDebit ? "Outgoing" : "Incoming";

  const cancelPendingWire = async () => {
    setIsCanceling(true);
    try {
      await axios.post("/proxy/api/fundstransfer/CancelPendingWire", {
        fileNumber: orderNumber,
        transactionId: transaction.id,
        wireType,
        canceledBy: user?.username?.replace(/^WORKGROUP\\/i, "") || user?.name,
        reason
      });
      setOpen(false);
      setReason("");
      refreshBalance();
      refreshTransactions();
    } finally {
      setIsCanceling(false);
    }
  };

  return (
      <>
        <Tooltip title={`Cancel pending ${wireType.toLowerCase()} wire`}>
          <IconButton color="error" size="small" onClick={() => setOpen(true)}>
            <Iconify icon="mdi:cancel-circle-outline"/>
          </IconButton>
        </Tooltip>
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Cancel Pending Wire</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <Typography variant="body2">
                Cancel this pending {wireType.toLowerCase()} wire for {fCurrency(transaction.amount)}?
              </Typography>
              <TextField
                  label="Reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  autoFocus
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={() => setOpen(false)} disabled={isCanceling}>
              Keep Wire
            </Button>
            <Button variant="contained" color="error" onClick={cancelPendingWire} disabled={!reason.trim() || isCanceling}>
              {isCanceling ? <CircularProgress size={20} color="inherit"/> : "Cancel Wire"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
  );
}

function EditPendingTransactionButton({
  orderNumber,
  transaction,
  refreshTransactions,
  refreshBalance
}: {
  orderNumber: string;
  transaction: LedgerType;
  refreshTransactions: () => void;
  refreshBalance: () => void;
}) {
  const [open, setOpen] = useState(false);
  const transactionKind =
      transaction.type === "Wire" || transaction.typeDescription?.toLowerCase().includes("wire") ? "wire" : "check";

  return (
      <>
        <Tooltip title={`Edit pending ${transactionKind}`}>
          <IconButton color="primary" size="small" onClick={() => setOpen(true)}>
            <Iconify icon="eva:edit-2-outline"/>
          </IconButton>
        </Tooltip>
        {open && (
            <EditPendingTransactionModal
                open={open}
                closeModal={() => setOpen(false)}
                orderNumber={orderNumber}
                transaction={transaction}
                handleRefreshList={refreshTransactions}
                handleRefreshBalance={refreshBalance}
            />
        )}
      </>
  );
}

function EditTransferButton({ orderNumber, transaction, refreshTransactions, refreshBalance }: {
  orderNumber: string; transaction: LedgerType; refreshTransactions: () => void; refreshBalance: () => void;
}) {
  const [open, setOpen] = useState(false);
  return <>
    <Tooltip title="Edit ledger transfer"><IconButton color="primary" size="small" onClick={() => setOpen(true)}><Iconify icon="eva:edit-2-outline" /></IconButton></Tooltip>
    {open && <EditLedgerTransferModal orderNumber={orderNumber} transaction={transaction} onClose={() => setOpen(false)} refresh={() => { refreshTransactions(); refreshBalance(); }} />}
  </>;
}

export const columns = (
    handleHistoryModal: (row: string) => void,
    orderNumber: string,
    refreshTransactions: () => void,
    refreshBalance: () => void
): MRT_ColumnDef<LedgerType>[] => [
  {
    header: 'Status',
    accessorKey: 'status',
    size: 20,
    maxSize: 20,
    Cell: ({renderedCellValue, row}) => {
      var color = statusColorMap[row.original.status];
      return (
          <Chip
              label={row.original.status}
              color={color}
              variant='soft'
              size='small'
              sx={{borderRadius: '4px'}}
              onClick={() => handleHistoryModal(row.original.id)}
          />
      );
    },
    filterVariant: 'select',
    filterSelectOptions: ['Pending', 'Posted', 'Voided', 'Deleted'],
    filterFn: (row, columnId, filterValue) => {
      return filterValue.includes(row.getValue(columnId));
    },
  },
  {
    header: 'Transaction Type',
    accessorKey: 'typeDescription',
    Cell: ({renderedCellValue, row}) => {
      const isPendingCheck = row.original.status === "Pending" && row.original.typeDescription === "Check";
      const isPendingWire =
          row.original.status === "Pending" &&
          (row.original.type === "Wire" || row.original.typeDescription?.toLowerCase().includes("wire"));
      const isEditablePendingTransaction = isPendingCheck || isPendingWire;

      return (
        <Stack direction='row' spacing={4} alignItems={'center'}>
          <Typography variant='body2'>
            {row.original.typeDescription}
          </Typography>
          {row.original.typeDescription === 'Check' && row.original.status.toLowerCase() !== 'voided' && 
              <CheckPrintIcon orderNumber={orderNumber} transactionId={row.original.id} refreshTransactions={refreshTransactions}/>
              
          }
          {["Posted", "Pending"].includes(row.original.status) && ["LedgerTransferIn", "LedgerTransferOut"].includes(row.original.type) && (
            <EditTransferButton orderNumber={orderNumber} transaction={row.original} refreshTransactions={refreshTransactions} refreshBalance={refreshBalance} />
          )}
          {isEditablePendingTransaction && (
              <EditPendingTransactionButton
                  orderNumber={orderNumber}
                  transaction={row.original}
                  refreshTransactions={refreshTransactions}
                  refreshBalance={refreshBalance}
              />
          )}
          {isPendingWire && (
              <CancelPendingWireButton
                  orderNumber={orderNumber}
                  transaction={row.original}
                  refreshTransactions={refreshTransactions}
                  refreshBalance={refreshBalance}
              />
          )}
         
          
        </Stack>
      );
    },
  },
  {
    header: 'In/Out',
    accessorKey: 'isDebit',
    Cell: ({renderedCellValue, row}) => (
        <Typography variant='body2'>
          {row.original.isDebit ? 'Out' : 'In'}
        </Typography>
    ),
    size: 20,
    maxSize: 20,
    filterVariant: 'select',
    filterSelectOptions: ['In', 'Out'],
    filterFn: (row, id, filterValue) => {
      var isDebit = filterValue === 'Out' && row.original.isDebit;
      var notDebit = filterValue === 'In' && !row.original.isDebit;
      return isDebit ? isDebit : notDebit;
    },
  },
  {
    header: 'Tx Date',
    accessorKey: 'transactionDate',
    Cell: ({renderedCellValue, row}) => (
        <Typography variant='body2'>
          {row.original.transactionDate && fMonthDayYearSlashNotation(row.original.transactionDate)}
        </Typography>
    ),
    filterVariant: 'date-range',
    filterFn: (row, id, filterValue) => {
      var startDate = new Date(filterValue[0]).getTime();
      var endDate = new Date(filterValue[1]).getTime();
      var transactionDate = new Date(row.original.transactionDate).getTime();
      var passes = transactionDate >= startDate && transactionDate <= endDate;
      return passes;
    },
  },
  {
    header: 'Amount',
    accessorKey: 'amount',
    size: 20,
    maxSize: 20,
    Cell: ({renderedCellValue, row}) => (
        <Typography variant='body2'>{fCurrency(row.original.amount)}</Typography>
    ),
  },
  {
    header: 'Ref #',
    accessorKey: 'referenceNumber',
    size: 20,
    maxSize: 20,
  },
  {
    header: 'Payor/Payee',
    accessorKey: 'payorPayee',
  },
  {
    header: 'Memo',
    accessorKey: 'memo',
    Cell: ({renderedCellValue, row}) => (
        <Typography sx={{whiteSpace: 'normal', width: 250}} variant='body2'>{row.original.memo}</Typography>
    ),
  },
  {
    header: 'Cleared/Voided',
    accessorKey: 'clearedOn',
    size: 20,
    maxSize: 20,
    Cell: ({renderedCellValue, row}) => (
        <Stack direction='row' spacing={2} alignItems="center">
          <div style={{width: 70}}>
            {row.original.clearedOn && (
                <Typography variant='body2'>
                  {fMonthDayYearSlashNotation(row.original.clearedOn)}{' '}
                </Typography>
            )}
            {row.original.voidedOn && (
                <Typography variant='body2'>
                  {fMonthDayYearSlashNotation(row.original.voidedOn)}{' '}
                </Typography>
            )}
          </div>
          <IconButton onClick={() => handleHistoryModal(row.original.id)}>
            <Iconify icon='bi:three-dots-vertical'/>
          </IconButton>
        </Stack>
    ),
  },
];
